/** Reporting tab for the Customer Workspace (Demo Mode only). Editable reporting requirements -
 * a small table since each row is a structured, repeatable record (report type, frequency,
 * format, next due date), bound to the parent workspace's edit draft (see `../index.tsx`).
 * Rendered inside the single Section the parent workspace places directly below its Tabs. */

import {
  Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { CustomerRecord } from '../../../../mocks/data/customerManagementData';

interface ReportingTabProps {
  customer: CustomerRecord;
  onChange: (updater: (prev: CustomerRecord) => CustomerRecord) => void;
}

const ReportingTab = ({ customer, onChange }: ReportingTabProps) => {
  const { t } = useTranslation('common');

  const updateRow = (index: number, patch: Partial<CustomerRecord['reporting'][number]>) => {
    onChange((prev) => ({ ...prev, reporting: prev.reporting.map((row, i) => (i === index ? { ...row, ...patch } : row)) }));
  };

  const removeRow = (index: number) => {
    onChange((prev) => ({ ...prev, reporting: prev.reporting.filter((_, i) => i !== index) }));
  };

  const addRow = () => {
    onChange((prev) => ({ ...prev, reporting: [...prev.reporting, { reportType: '', frequency: '', format: '', nextDue: '' }] }));
  };

  return (
    <Section>
      <TableHeader>
        {t('reportingTableTitle', 'Client reporting expectations')}
        <Pill text={`${customer.reporting.length} ${t('reportingCountLabel', 'requirements')}`} />
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="secondary"
            size="md"
            startIcon={<Add />}
            onClick={addRow}>
            {t('reportingAddRow', 'Add reporting requirement')}
          </Button>
        </Box>
      </TableHeader>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('reportingReportType', 'Report Type')}</TableCell>
              <TableCell>{t('reportingFrequency', 'Frequency')}</TableCell>
              <TableCell>{t('reportingFormat', 'Format')}</TableCell>
              <TableCell>{t('reportingNextDue', 'Next Due')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {customer.reporting.length === 0 && (
              <TableRow><TableCell colSpan={5}>{t('reportingNotStarted', 'Reporting requirements not yet agreed with client.')}</TableCell></TableRow>
            )}
            {customer.reporting.map((requirement, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    value={requirement.reportType}
                    onChange={(e) => updateRow(index, { reportType: e.target.value })}
                    size="small"
                    fullWidth
                    sx={{ mb: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={requirement.frequency}
                    onChange={(e) => updateRow(index, { frequency: e.target.value })}
                    size="small"
                    fullWidth
                    sx={{ mb: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={requirement.format}
                    onChange={(e) => updateRow(index, { format: e.target.value })}
                    size="small"
                    fullWidth
                    sx={{ mb: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="date"
                    value={requirement.nextDue}
                    onChange={(e) => updateRow(index, { nextDue: e.target.value })}
                    size="small"
                    fullWidth
                    sx={{ mb: 0 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small"
                    onClick={() => removeRow(index)}>
                    <DeleteOutline fontSize="small"
                      color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default ReportingTab;
